import React, { createContext, useContext, useState, useEffect } from 'react';
import { AuthService, User } from '../services/auth';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name?: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
  isTokenSynced: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

const AUTH_STORAGE_KEY = 'auth_user';
const TOKEN_STORAGE_KEY = 'auth_token';
const TOKEN_EXPIRY_KEY = 'auth_token_expiry';
const TOKEN_SYNC_TIMEOUT = 60000; // 5 seconds timeout for token sync
const TOKEN_SYNC_RETRY_DELAY = 10000; // 1 second between retries
const MAX_SYNC_RETRIES = 3;

export function AuthProvider(props: { children: React.ReactNode }) {
  // Safely access children, providing a default empty fragment if props is undefined
  const { children = <></> } = props || {};
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTimeout, setRefreshTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isTokenSynced, setIsTokenSynced] = useState(false);
  const [syncRetryCount, setSyncRetryCount] = useState(0);

  // Token synchronization — validates current token against the server
  const syncToken = async (retryCount = 0): Promise<boolean> => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (!token || !tokenExpiry) {
      return false;
    }

    try {
      const expiryTime = parseInt(tokenExpiry, 10);
      if (Date.now() >= expiryTime) {
        const refreshed = await AuthService.refreshSession();
        if (!refreshed) throw new Error('Token refresh failed during sync');
        return true;
      }

      // Race with a proper cancellable timeout to avoid unhandled rejections
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      const isValid = await Promise.race([
        AuthService.validateSession(),
        new Promise<boolean>((_, reject) => {
          timeoutId = setTimeout(
            () => reject(new Error('Token validation timed out')),
            15000
          );
        }),
      ]).finally(() => {
        if (timeoutId !== null) clearTimeout(timeoutId);
      });

      return isValid === true;
    } catch (error) {
      console.error('[Auth] Token sync error:', error);

      if (retryCount < MAX_SYNC_RETRIES) {
        await new Promise<void>((resolve) => setTimeout(resolve, TOKEN_SYNC_RETRY_DELAY));
        return syncToken(retryCount + 1);
      }

      return false;
    }
  };

  // Improved user and token persistence
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const savedUserData = localStorage.getItem(AUTH_STORAGE_KEY);
        const savedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
        const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

        if (savedUserData && savedToken && tokenExpiry) {
          const parsedUser = JSON.parse(savedUserData);
          const expiryTime = parseInt(tokenExpiry, 10);

          if (Date.now() < expiryTime) {
            if (isValidUser(parsedUser)) {
              setUser(parsedUser);
              console.log('[Auth] User restored from storage:', { id: parsedUser.id });
              
              // Start token sync process
              const syncResult = await syncToken();
              setIsTokenSynced(syncResult);
              
              if (syncResult) {
                setupTokenRefresh(expiryTime);
              } else {
                console.warn('[Auth] Token sync failed during initialization');
                clearAuthData();
              }
            } else {
              console.warn('[Auth] Invalid user data in storage, clearing...');
              clearAuthData();
            }
          } else {
            console.log('[Auth] Stored token expired, clearing auth data');
            clearAuthData();
          }
        }
      } catch (err) {
        console.error('[Auth] Failed to restore user from storage:', err);
        clearAuthData();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Validate user object structure
  const isValidUser = (user: any): user is User => {
    return (
      user &&
      typeof user === 'object' &&
      typeof user.id === 'string' &&
      user.id.trim().length > 0 &&
      typeof user.email === 'string' &&
      user.email.includes('@')
    );
  };

  // Setup token refresh timer with improved error handling
  const setupTokenRefresh = (expiryTime: number) => {
    if (refreshTimeout) {
      clearTimeout(refreshTimeout);
    }

    const timeUntilRefresh = expiryTime - Date.now() - (5 * 60 * 1000); // Refresh 5 minutes before expiry
    if (timeUntilRefresh > 0) {
      const timeout = setTimeout(async () => {
        try {
          const refreshed = await AuthService.refreshSession();
          if (refreshed?.token) {
            const newExpiryTime = Date.now() + (24 * 60 * 60 * 1000);
            localStorage.setItem(TOKEN_STORAGE_KEY, refreshed.token);
            localStorage.setItem(TOKEN_EXPIRY_KEY, newExpiryTime.toString());
            setupTokenRefresh(newExpiryTime);
          } else {
            throw new Error('Token refresh failed');
          }
        } catch (error) {
          console.error('[Auth] Token refresh failed:', error);
          clearAuthData();
          setError('Session expired. Please log in again.');
        }
      }, timeUntilRefresh);
      
      setRefreshTimeout(timeout);
      console.log('[Auth] Token refresh scheduled in', Math.round(timeUntilRefresh / 1000), 'seconds');
    }
  };

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    setIsTokenSynced(false);
    
    try {
      const response = await AuthService.login({ email, password });
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      const { token, ...userData } = response.data;
      
      if (!isValidUser(userData)) {
        throw new Error('Invalid user data received from server');
      }
      
      // Calculate token expiry (24 hours from now)
      const expiryTime = Date.now() + (24 * 60 * 60 * 1000);
      
      // Store auth data
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
      
      setUser(userData);
      
      // Add a small delay before verifying token to ensure it's properly stored
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Verify token synchronization
      const syncResult = await syncToken();
      if (!syncResult) {
        console.warn('[Auth] Initial token sync failed, retrying...');
        // Try one more time after a short delay
        await new Promise(resolve => setTimeout(resolve, 500));
        const retryResult = await syncToken();
        if (!retryResult) {
          throw new Error('Failed to synchronize authentication token');
        }
      }
      
      setIsTokenSynced(true);
      
      // Setup token refresh
      setupTokenRefresh(expiryTime);
      
      console.log('[Auth] User logged in successfully:', { id: userData.id });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      console.error('[Auth] Login failed:', err);
      setError(errorMessage);
      clearAuthData();
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, password: string, name?: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await AuthService.register({ email, password, name });
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      const { token, ...userData } = response.data;
      
      if (!isValidUser(userData)) {
        throw new Error('Invalid user data received from server');
      }
      
      // Calculate token expiry (24 hours from now)
      const expiryTime = Date.now() + (24 * 60 * 60 * 1000);
      
      // Store auth data
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
      
      setUser(userData);
      setIsTokenSynced(true);
      
      // Setup token refresh
      setupTokenRefresh(expiryTime);
      
      console.log('[Auth] User registered successfully:', { id: userData.id });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed';
      console.error('[Auth] Registration failed:', err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const clearAuthData = () => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    if (refreshTimeout) {
      clearTimeout(refreshTimeout);
      setRefreshTimeout(null);
    }
    setUser(null);
    setIsTokenSynced(false);
    setSyncRetryCount(0);
  };

  const logout = () => {
    try {
      clearAuthData();
      console.log('[Auth] User logged out');
    } catch (err) {
      console.error('[Auth] Logout error:', err);
    }
  };

  const clearError = () => setError(null);

  // Calculate isAuthenticated based on user and token sync status
  const isAuthenticated = user !== null && isTokenSynced;

  // FIXED: Session check interval with stable references and reduced frequency
  useEffect(() => {
    let isMounted = true;
    let interval: NodeJS.Timeout | null = null;
    
    const checkSession = async () => {
      if (!isMounted) return;
      
      const savedUserData = localStorage.getItem(AUTH_STORAGE_KEY);
      const savedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
      const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

      if ((!savedUserData || !savedToken || !tokenExpiry) && user) {
        console.warn('[Auth] Auth data missing from storage, logging out');
        if (isMounted) logout();
        return;
      }

      // Check token expiry
      if (tokenExpiry && Date.now() > parseInt(tokenExpiry, 10)) {
        console.warn('[Auth] Token expired, logging out');
        if (isMounted) logout();
        return;
      }

      // FIXED: Reduced frequency token sync check to prevent render loops
      if (user && !isTokenSynced) {
        try {
          const syncResult = await syncToken();
          if (isMounted) {
            setIsTokenSynced(syncResult);
            if (!syncResult) {
              console.warn('[Auth] Token sync check failed, logging out');
              logout();
            }
          }
        } catch (error) {
          console.error('[Auth] Error during token sync check:', error);
          if (!isMounted) return;
          
          // Don't automatically logout on network errors
          if (error instanceof TypeError && error.message === 'Failed to fetch') {
            console.warn('[Auth] Network error during token sync, will retry later');
          } else {
            console.warn('[Auth] Critical error during token sync, logging out');
            logout();
          }
        }
      }
    };

    // FIXED: Delay initial check to prevent race conditions during component mount
    const initialTimeout = setTimeout(() => {
      if (isMounted) {
        checkSession();
        // FIXED: Increased interval to 5 minutes to reduce React re-render frequency
        interval = setInterval(checkSession, 300000); // Check every 5 minutes instead of 1
      }
    }, 1000);

    return () => {
      isMounted = false;
      clearTimeout(initialTimeout);
      if (interval) {
        clearInterval(interval);
      }
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
    };
  }, []); // FIXED: Empty dependency array to prevent infinite loops

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        login, 
        logout, 
        register,
        isLoading,
        error,
        clearError,
        isTokenSynced,
        isAuthenticated
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}